import anthropicClient from './anthropicClient';

/**
 * Maps Anthropic API error status codes to user-friendly error messages.
 * @param {number} statusCode - The HTTP status code from Anthropic API.
 * @param {Object} errorData - The error data from API response.
 * @returns {Object} Object with isInternal flag and error message.
 */
function getErrorMessage(statusCode, errorData) {
  if (statusCode === 401) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your Anthropic API key.' };
  } else if (statusCode === 403) {
    return { isInternal: true, message: 'Permission denied. Your API key does not have access to the specified resource.' };
  } else if (statusCode === 404) {
    return { isInternal: true, message: 'Resource not found. The requested endpoint or model may not exist.' };
  } else if (statusCode === 429) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (statusCode === 500) {
    return { isInternal: true, message: 'Anthropic service error. An unexpected error occurred on their servers. Please try again later.' };
  } else if (statusCode === 529) {
    return { isInternal: true, message: 'Anthropic service is temporarily overloaded. Please try again in a few moments.' };
  } else {
    return { isInternal: false, message: errorData?.error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Generates platform-optimized social media post with brand voice and performance predictions
 * @param {Object} options - Post generation options
 * @param {string} options.topic - Main topic or theme for the post
 * @param {string} options.tone - Tone of the post (professional, casual, humorous, etc.)
 * @param {string} options.platform - Target social media platform
 * @param {number} options.maxLength - Maximum character length
 * @param {string[]} options.keywords - Keywords to include in the post
 * @param {Object} options.brandVoice - Brand voice guidelines
 * @param {string} options.brandVoice.description - Brand voice description
 * @param {string[]} options.brandVoice.values - Brand values
 * @param {string[]} options.brandVoice.dosList - Things the brand does
 * @param {string[]} options.brandVoice.dontsList - Things the brand avoids
 * @returns {Promise<Object>} Generated post with metadata and performance predictions
 */
export async function generatePost(options) {
  const { 
    topic, 
    tone = 'professional', 
    platform = 'general', 
    maxLength = 280, 
    keywords = [],
    brandVoice = null
  } = options;

  try {
    const keywordText = keywords?.length > 0 ? ` Include these keywords naturally: ${keywords?.join(', ')}.` : '';
    
    // Platform-specific optimization guidelines
    const platformGuidelines = {
      twitter: 'Use concise language, trending hashtags (2-3 max), and conversational tone. Optimize for retweets and replies.',
      linkedin: 'Professional tone, industry insights, thought leadership. Use 3-5 relevant hashtags. Optimize for engagement and shares.',
      facebook: 'Conversational, community-focused, encourage comments. Use emojis sparingly. Optimize for reactions and shares.',
      instagram: 'Visual storytelling focus, emoji-friendly, 5-10 hashtags. Optimize for saves and shares.',
      tiktok: 'Trendy, authentic, hook in first 3 seconds. Use trending sounds/hashtags. Optimize for shares and duets.',
      general: 'Balanced approach suitable for multiple platforms.'
    };

    const platformGuide = platformGuidelines?.[platform?.toLowerCase()] || platformGuidelines?.general;

    // Brand voice integration
    let brandVoicePrompt = '';
    if (brandVoice) {
      brandVoicePrompt = `\n\nBrand Voice Guidelines:
- Voice Description: ${brandVoice?.description || 'Not specified'}
- Brand Values: ${brandVoice?.values?.join(', ') || 'Not specified'}
- Do: ${brandVoice?.dosList?.join(', ') || 'Not specified'}
- Don't: ${brandVoice?.dontsList?.join(', ') || 'Not specified'}

Ensure the post aligns with these brand voice guidelines while maintaining authenticity.`;
    }
    
    const prompt = `You are an expert social media strategist specializing in ${platform} content optimization.

Create a high-performing ${platform} post about "${topic}".

Requirements:
- Tone: ${tone}
- Maximum length: ${maxLength} characters
- Platform optimization: ${platformGuide}${keywordText}${brandVoicePrompt}

After generating the post, provide a performance prediction analysis.

Respond in the following JSON format:
{
  "post": "[the optimized post content]",
  "performancePrediction": {
    "engagementScore": <number 0-100>,
    "viralityPotential": "<low|medium|high>",
    "predictedReach": "<estimated reach description>",
    "bestTimeToPost": "<optimal posting time for platform>",
    "strengths": ["strength 1", "strength 2", "strength 3"],
    "optimizationTips": ["tip 1", "tip 2"]
  },
  "platformOptimizations": {
    "hashtagStrategy": "<hashtag recommendations>",
    "callToAction": "<recommended CTA>",
    "contentFormat": "<format recommendations>"
  }
}`;

    const response = await anthropicClient?.post('/messages', {
      model: 'claude-sonnet-4-5-20250929',
      messages: [
        { 
          role: 'user', 
          content: prompt 
        }
      ],
      max_tokens: 2000,
      temperature: 0.7,
    });

    const text = response?.data?.content?.[0]?.text?.trim();
    
    // Extract JSON from response
    const jsonMatch = text?.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('Invalid response format from Claude');
    }
    
    const result = JSON.parse(jsonMatch?.[0]);
    const content = result?.post;

    return {
      content,
      metadata: {
        topic,
        tone,
        platform,
        generatedAt: new Date()?.toISOString(),
        characterCount: content?.length,
        provider: 'anthropic',
        model: 'claude-sonnet-4-5'
      },
      performancePrediction: result?.performancePrediction,
      platformOptimizations: result?.platformOptimizations,
      brandVoiceApplied: !!brandVoice
    };
  } catch (error) {
    if (error?.response) {
      const errorInfo = getErrorMessage(error?.response?.status, error?.response?.data);
      const err = new Error(errorInfo?.message);
      err.statusCode = error?.response?.status;
      
      if (!errorInfo?.isInternal) {
        console.error('Anthropic API error:', err);
      }
      throw err;
    }
    
    console.error('Error generating post with Claude:', error);
    throw new Error('Failed to generate post content. Please try again.');
  }
}

/**
 * Generates multiple post variations with performance predictions
 * @param {Object} options - Post generation options
 * @param {number} variations - Number of variations to generate
 * @returns {Promise<Array>} Array of generated post variations with predictions
 */
export async function generatePostVariations(options, variations = 3) {
  try {
    const posts = await Promise.all(
      Array.from({ length: variations }, () => generatePost(options))
    );
    return posts;
  } catch (error) {
    throw error;
  }
}

/**
 * Analyzes post content and suggests improvements using Claude
 * @param {string} content - Post content to analyze
 * @param {string} platform - Target platform
 * @param {Object} brandVoice - Optional brand voice guidelines
 * @returns {Promise<Object>} Analysis and suggestions
 */
export async function analyzePost(content, platform = 'general', brandVoice = null) {
  try {
    let brandVoiceContext = '';
    if (brandVoice) {
      brandVoiceContext = `\n\nBrand Voice Context:
- Voice: ${brandVoice?.description || 'Not specified'}
- Values: ${brandVoice?.values?.join(', ') || 'Not specified'}

Evaluate brand voice consistency in your analysis.`;
    }

    const prompt = `Analyze this ${platform} post and provide comprehensive feedback with performance predictions:

"${content}"${brandVoiceContext}

Provide your analysis in the following JSON format:
{
  "strengths": ["strength 1", "strength 2", ...],
  "improvements": ["improvement 1", "improvement 2", ...],
  "suggestions": ["suggestion 1", "suggestion 2", ...],
  "overallScore": <number between 0 and 10>,
  "brandVoiceAlignment": <number between 0 and 10>,
  "performancePrediction": {
    "engagementScore": <number 0-100>,
    "viralityPotential": "<low|medium|high>",
    "predictedReach": "<estimated reach description>"
  }
}`;

    const response = await anthropicClient?.post('/messages', {
      model: 'claude-sonnet-4-5-20250929',
      messages: [
        { 
          role: 'user', 
          content: prompt 
        }
      ],
      max_tokens: 1500,
      temperature: 0.5,
    });

    const text = response?.data?.content?.[0]?.text?.trim();
    
    // Extract JSON from response
    const jsonMatch = text?.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('Invalid response format from Claude');
    }
    
    return JSON.parse(jsonMatch?.[0]);
  } catch (error) {
    if (error?.response) {
      const errorInfo = getErrorMessage(error?.response?.status, error?.response?.data);
      const err = new Error(errorInfo?.message);
      err.statusCode = error?.response?.status;
      
      if (!errorInfo?.isInternal) {
        console.error('Anthropic API error:', err);
      }
      throw err;
    }
    
    console.error('Error analyzing post with Claude:', error);
    throw new Error('Failed to analyze post content. Please try again.');
  }
}